import type { Identifiants, Session } from '@rosetta/shared';
import { z } from 'zod';
import { expireLeApres } from '../../session';
import type { ConfigTahiti } from '../config';
import {
  ContexteTravailRequisError,
  IdentifiantsInvalidesError,
  ServiceIndisponibleError,
} from '../erreurs';
import { analyser } from './analyse';
import type { AppelHttp } from './http';

/**
 * Authentication calls (contract `auth_service` of Back2Tahiti, confirmed by the reference
 * script of the team, Rosetta v5):
 *
 *   POST <VITE_AUTH_SERVICE_URL>/login                    { email, password }
 *   GET  <VITE_AUTH_SERVICE_URL>/auth/work-contexts       (only when the login lists none)
 *   POST <VITE_AUTH_SERVICE_URL>/auth/select-work-context { workContextId }
 *
 * The session lasts as long as the `expiresIn` (seconds) of the answer; the application
 * imposes no lifetime of its own.
 *
 * Work context: the configured one (`VITE_ROSETTA_WORK_CONTEXT_ID`) is selected; without
 * one, the single context of the account is selected on its own; with several and none
 * configured, the login fails with `ContexteTravailRequisError`. The token of the selection
 * replaces the one of the login. An account with no context keeps the token of the login.
 */

export const requeteLoginSchema = z.object({
  email: z.string().min(1),
  password: z.string().min(1),
});

export const reponseLoginSchema = z.object({
  success: z.boolean().optional(),
  token: z.string().min(1),
  user: z.object({
    id: z.union([z.string().min(1), z.number()]).transform(String),
    email: z.string().email(),
    permissions: z.array(z.unknown()).optional(),
  }),
  expiresIn: z.number().positive().optional(),
});

export const requeteContexteSchema = z.object({
  /** The contract shows a number; a configured identifier made of digits is sent as one. */
  workContextId: z.union([z.string().min(1), z.number()]),
});

/** The answer may carry a new token (and its lifetime) for the selected context. */
export const reponseContexteSchema = z.object({
  token: z.string().min(1).optional(),
  expiresIn: z.number().positive().optional(),
});

const identifiantContexteSchema = z.union([z.string().min(1), z.number()]);

/**
 * One work context, as the login or the list of contexts writes it. The reference script
 * accepts three spellings of the identifier (`workContextId`, `work_context_id`, `id`) and
 * three of the label: so does the client.
 */
const contexteSchema = z
  .object({
    workContextId: identifiantContexteSchema.nullish(),
    work_context_id: identifiantContexteSchema.nullish(),
    id: identifiantContexteSchema.nullish(),
    title: z.string().nullish(),
    name: z.string().nullish(),
    code: z.string().nullish(),
  })
  .transform((contexte) => ({
    id: String(contexte.workContextId ?? contexte.work_context_id ?? contexte.id ?? '') || null,
    titre: contexte.title ?? contexte.name ?? contexte.code ?? null,
  }));
const listeContextesSchema = z.array(contexteSchema);
type ContexteTravail = z.infer<typeof contexteSchema>;

const enveloppeContextesSchema = z.object({
  workContexts: z.unknown().optional(),
  work_contexts: z.unknown().optional(),
  contexts: z.unknown().optional(),
  data: z.unknown().optional(),
});

/**
 * The work contexts an answer lists: the answer itself when it is a list, otherwise the
 * first list among `workContexts`, `work_contexts`, `contexts`, `data` and
 * `data.workContexts`. Anything else is "no context".
 */
function contextesDe(brute: unknown): ContexteTravail[] {
  const enveloppe = enveloppeContextesSchema.safeParse(brute);
  const imbrique = enveloppe.success
    ? enveloppeContextesSchema.safeParse(enveloppe.data.data)
    : undefined;
  const candidats = Array.isArray(brute)
    ? [brute]
    : enveloppe.success
      ? [
          enveloppe.data.workContexts,
          enveloppe.data.work_contexts,
          enveloppe.data.contexts,
          enveloppe.data.data,
          imbrique?.success === true ? imbrique.data.workContexts : undefined,
        ]
      : [];
  const liste = candidats.find((candidat) => Array.isArray(candidat));
  const lus = listeContextesSchema.safeParse(liste);
  return lus.success ? lus.data : [];
}

/**
 * Signs in. Wrong credentials (a 400, 401 or 403, or `success: false`) raise
 * `IdentifiantsInvalidesError`, which never says which of the two is wrong.
 */
export async function login(
  appel: AppelHttp,
  config: ConfigTahiti,
  identifiants: Identifiants,
  maintenant: number = Date.now(),
): Promise<Session> {
  const requete = requeteLoginSchema.safeParse({
    email: identifiants.email.trim(),
    password: identifiants.motDePasse,
  });
  // Empty credentials cannot be right: no need to ask Tahiti.
  if (!requete.success) throw new IdentifiantsInvalidesError();
  const corps = requete.data;

  const brute = await appel({
    methode: 'POST',
    url: `${config.urlAuth}/login`,
    corps,
    surRefus: () => new IdentifiantsInvalidesError(),
  });
  // TO VALIDATE(#2): a refused login answers 401 here, with `success: false` and no
  // token. Both cases are handled; the real status code is to be confirmed.
  if (
    typeof brute === 'object' &&
    brute !== null &&
    'success' in brute &&
    brute.success === false
  ) {
    throw new IdentifiantsInvalidesError();
  }
  const reponse = analyser(reponseLoginSchema, brute, 'login');

  let jeton = reponse.token;
  let dureeSecondes = reponse.expiresIn;

  const contexte = await selectionnerContexte(appel, config, jeton, brute);
  if (contexte !== null) {
    jeton = contexte.token ?? jeton;
    dureeSecondes = contexte.expiresIn ?? dureeSecondes;
  }

  return {
    jeton,
    expireLe: expireLeApres(dureeSecondes, maintenant),
    utilisateur: { id: reponse.user.id, email: reponse.user.email },
  };
}

/**
 * Reads the work contexts of the account when the login did not list them. As in the
 * reference script, a read that fails is "no context": the login then goes on with its own
 * token, and H2Data says so if it needed a context.
 */
async function lireContextes(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
): Promise<ContexteTravail[]> {
  try {
    const brute = await appel({
      methode: 'GET',
      url: `${config.urlAuth}/auth/work-contexts`,
      jeton,
      surRefus: (statut) =>
        new ServiceIndisponibleError(`Tahiti refused the list of work contexts (status ${statut})`),
    });
    return contextesDe(brute);
  } catch (erreur) {
    if (erreur instanceof ServiceIndisponibleError) return [];
    throw erreur;
  }
}

/**
 * Selects the work context and returns the answer, whose new `token` (when there is one)
 * replaces the one of the login; `null` when the account has no context to select.
 * Credentials were accepted at this point, so a refusal is a rights or configuration
 * problem, not a wrong password.
 *
 * `loginBrut` is the answer of the login, which may already list the contexts.
 */
export async function selectionnerContexte(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
  loginBrut: unknown,
): Promise<z.infer<typeof reponseContexteSchema> | null> {
  let identifiant: string | number | null = config.contexteTravailId ?? null;

  if (identifiant === null) {
    let contextes = contextesDe(loginBrut);
    if (contextes.length === 0) contextes = await lireContextes(appel, config, jeton);
    if (contextes.length > 1) throw new ContexteTravailRequisError(contextes);
    identifiant = contextes[0]?.id ?? null;
  }
  if (identifiant === null) return null;

  const corps = requeteContexteSchema.parse({
    workContextId:
      typeof identifiant === 'string' && /^\d+$/.test(identifiant)
        ? Number(identifiant)
        : identifiant,
  });
  const brute = await appel({
    methode: 'POST',
    url: `${config.urlAuth}/auth/select-work-context`,
    jeton,
    corps,
    surRefus: (statut) =>
      new ServiceIndisponibleError(`Tahiti refused the work context (status ${statut})`),
  });
  return analyser(reponseContexteSchema, brute, 'select-work-context');
}
